<template>
  <div class="file-panel-selection file-panel-selection__side-panel-root">
    <template v-if="hasTasksInProgress">
      <pui-flex justifyContent="center" alignItems="center" class="has-tasks-in-progress">
        <!-- This svg corresponds to mdi-timer-sand -->
        <!-- We need to use the svg instead of the font because we want -->
        <!-- to rotate it around its center (font messes up the center) -->
        <svg viewBox="0 0 24 24" class="has-tasks-in-progress-icon">
          <path
            d="M6,2H18V8H18V8L14,12L18,16V16H18V22H6V16H6V16L10,12L6,8V8H6V2M16,16.5L12,12.5L8,16.5V20H16V16.5M12,11.5L16,7.5V4H8V7.5L12,11.5M10,6H14V6.75L12,8.75L10,6.75V6Z"
          />
        </svg>
      </pui-flex>
    </template>
    <template v-else-if="files && files.length > 0">
      <pui-flex v-if="showPanelControls" class="control-container">
        <pui-button
          @click="toggleOpenPanel"
          class="toggle-open-button"
          picto="mdi-arrow-top-left-bottom-right"
          flat
          variant="secondary"
        />
        <pui-flex class="control-container__title control-container__title-bold">
          {{
            $t('dam.files.panel_selection.title.elements', {
              title,
              count: files.length,
            })
          }}
        </pui-flex>
      </pui-flex>
      <pui-flex v-scrollable.y class="panel-selection-side-panel-content" direction="column">
        <div class="top-container">
          <div class="file-panel-selection-actions">
            <pui-button
              v-if="showOpenSelectionDialog"
              @click.stop="openSelectionDialog"
              variant="primary"
            >
              {{ $t('dam.files.panel_selection.actions.open_selection') }}
            </pui-button>

            <pui-button
              @click.stop="downloadFiles"
              v-permission="Permissions.Files.DOWNLOAD"
              variant="primary"
            >
              {{ $t('dam.files.panel_selection.actions.download') }}
            </pui-button>
          </div>
          <div :style="{ height: `${CAROUSEL_HEIGHT}px` }" class="panel-selection-top-thumbs">
            <template v-if="carouselFiles.length > 1">
              <pui-carousel
                ref="carousel"
                :items="carouselFiles"
                :options="carouselOptions"
                :getItemId="getFileId"
                class="panel-selection__carousel"
              >
                <template #default="{ item }">
                  <pui-dam-file-viewer
                    :file="item"
                    :style="getThumbnailStyle(item)"
                    class="panel-selection-top-thumb"
                    classImg="panel-selection-top-thumb-img"
                  />
                </template>
              </pui-carousel>
            </template>
            <template v-else-if="carouselFiles.length === 1">
              <pui-dam-file-viewer
                :file="carouselFiles[0]"
                :style="getThumbnailStyle(carouselFiles[0])"
                class="panel-selection-top-thumb"
                classImg="panel-selection-top-thumb-img"
              />
            </template>
          </div>
        </div>
        <pui-flex
          v-if="files.length < this.maxItemsDisplay"
          class="panel-selection-content-bottom"
          direction="column"
          flex="1"
        >
          <pui-common-spinner
            ref="masonrySpinner"
            v-if="attributesAreLoading || saveInProgress"
            :size="20"
            :strokeWidth="2"
            :hideMessage="true"
            class="panel-selection__attributes-spinner"
            position="fixed"
          />
          <div v-if="!attributesAreLoading && getAttributesError" class="panel-selection__error">
            <p class="panel-selection__error-message">
              {{ $t('dam.files.panel_selection.attributes.error.primary') }}
            </p>
            <p @click="getFormAndValues" class="panel-selection__error-reload pui-link">
              {{ $t('dam.files.panel_selection.attributes.error.reload') }}
            </p>
          </div>

          <!-- - It is important to only render tabbed-menu if we can pass it all 'tabs' & 'prependExtraTabs' -->
          <!-- If one of them are not fully loaded, the component might set a dynamic/selected tab from one of the two -->
          <!-- arrays, since the other was not loaded -->
          <!-- - We must add a unique key on the tabs, using the sourceFiles ids: -->
          <!-- since the rendered tabs are not responsive to sourceFiles -->
          <!-- itemId changes, they may show calculated data for the old sourceFiles -->
          <pui-tabbed-menu
            ref="attributeTabs"
            v-if="!attributesAreLoading && !getAttributesError && formDefinitionTabs"
            :key="sourceFiles.map(f => f.itemId).join('_')"
            :tabs="formDefinitionTabs"
            :getTabId="getTabId"
            :appendExtraTabs="extraTabs"
            :initialDynamicTabId="currentDynamicTabId"
            :initialActiveTabId="activeAttributeTabId"
            :minInlineTabCount="nbMinTabs"
            :maxInlineTabCount="nbMaxTabs"
            @changeDynamicTab="onChangeDynamicTab"
            @tabChange="onSelectAttributeTabChange"
            @clearSelectedTab="onClearSelectedAttributeTab"
            class="panel-selection-tabs"
            classBody="panel-selection-tabs-body"
            classTab="panel-selection-tab"
          >
            <template #headerTitle="{ tab }">
              {{ tab._titleKey ? $t(tab._titleKey) : piivoTranslate(tab) }}
            </template>

            <template #default="{ tab }">
              <!-- Render the file details -->
              <template v-if="getTabId(tab) === getTabId(DETAILS_TAB)">
                <div class="file-panel-selection-tabs-info">
                  <p
                    v-for="info in itemTabDefinition"
                    class="file-panel-selection-tabs-info-content"
                  >
                    <span class="file-panel-selection-tabs-info-content__label">
                      {{ $tc(info.labelKey, files.length) }}</span
                    >
                    <span class="file-panel-selection-tabs-info-content__value">{{
                      computeFileInfos(info)
                    }}</span>
                  </p>
                </div>
              </template>
              <!-- Render the file's classifications -->
              <template v-else-if="getTabId(tab) === getTabId(CLASSIFICATIONS_TAB)">
                <pui-file-classifications-tab
                  ref="classificationsTab"
                  :sourceFiles="sourceFiles"
                  :updatedFiles="updatedFiles"
                  :draggingFolder="draggingFolder"
                  @moveChanged="moveChanged"
                ></pui-file-classifications-tab>
              </template>
              <!-- Or render the attributes of the tab -->
              <template v-else>
                <pui-multiple-attribute-panel
                  ref="multipleAttributePanel"
                  v-for="attribute in tab.attributes"
                  :key="attribute.itemId"
                  :attribute="attribute"
                  :value="null"
                  :type="attribute.type"
                  :loadOptionsFunction="getPossibleValues"
                  :importExternalFiles="importExternalFiles"
                  :sourceFiles="sourceFiles"
                  :updatedFiles="updatedFiles"
                  :forceExtendedLink="isExpanded ? { value: true } : null"
                  @valueChanged="attributeValueChanged(attribute, $event)"
                />
              </template>
            </template>
          </pui-tabbed-menu>

          <div v-if="showActionButtons" class="file-panel-selection-tabs__info-actions-bottom">
            <pui-button @click="abandonChanges" variant="secondary" outline>
              {{ $t('dam.files.panel_selection.attributes.save.cancel') }}
            </pui-button>
            <pui-button
              @click="executeConfirmableBreakingSave()"
              :disabled="attributeValuesAreInvalid"
              variant="primary"
              class="save-button"
            >
              <span class="save-text">
                {{ $t('dam.files.panel_selection.attributes.save.confirm') }}
              </span>

              <template v-if="importExternalFileCount > 0">
                <span class="spacer"></span>
                <svg viewBox="0 0 24 24" class="wait-save-icon">
                  <path
                    fill="currentColor"
                    d="M6,2V8H6V8L10,12L6,16V16H6V22H18V16H18V16L14,12L18,8V8H18V2H6M16,16.5V20H8V16.5L12,12.5L16,16.5M12,11.5L8,7.5V4H16V7.5L12,11.5Z"
                  />
                </svg>
              </template>
            </pui-button>
          </div>
        </pui-flex>
        <pui-flex v-else class="panel-selection-content-bottom" direction="column" flex="1">
          <div class="max-items-primary-message">
            {{ $t('dam.files.panel_selection.max_items.primary_message') }}
          </div>
          <div class="max-items-secondary-message">
            {{ $t('dam.files.panel_selection.max_items.secondary_message') }}
          </div>
        </pui-flex>
      </pui-flex>
    </template>

    <portal :selector="dialogSelector">
      <pui-unsaved-attributes-dialog
        ref="unsavedAttributesDialog"
        :importInProgress="importExternalFileCount > 0"
      />
      <pui-file-deletion-confirmation-dialog
        ref="deletionConfirmationDialog"
      ></pui-file-deletion-confirmation-dialog>
    </portal>
  </div>
</template>
<script>
import Vue from 'vue';

import services from '../../../../../core/services';
import { AttributeTypes } from '../../../../common/constants';
import { DAM_APP_NAME } from '../../../constants';
import { FILE_PANEL_MAX_ITEMS_DISPLAY, FileTaskStatus } from '../../../constants/file';
import Permissions from '../../../constants/permissions';
import { validateAttributes } from '../../../helpers/fileHelper';
import {
  getAttributeLinkOptions,
  getFormAndValuesByItemsIds,
} from '../../../helpers/formAttributes';
import {
  computeFileInfos,
  multipleItemTabDefinition,
  singleItemTabDefinition,
} from '../../../helpers/infosTabDefinition';
import { getDamService } from '../../../services';
import { FileDownloadManager } from '../../../services/fileDownloadManager';
import { FileUpdateManager } from '../../../services/fileUpdateManager';
import puiDamFileViewer from '../../files/DamFileViewer';
import puiFileDeletionConfirmationDialog from '../move/FileDeletionConfirmationDialog';
import puiFileClassificationsTab from './FileClassificationsTab';

/**
 * ID for the details tab
 */
const DETAILS_TAB_ID = 'details_tab';

/**
 * ID for the classifications tab
 */
const CLASSIFICATIONS_TAB_ID = 'classifications_tab';

const CLASSIFICATIONS_TAB = {
  itemId: CLASSIFICATIONS_TAB_ID,
  _titleKey: 'dam.files.panel_selection.attributes.tabs.classifications.title',
};
const DETAILS_TAB = {
  itemId: DETAILS_TAB_ID,
  _titleKey: 'dam.files.panel_selection.attributes.tabs.details.title',
};

const CAROUSEL_HEIGHT = 144;

export default {
  components: {
    puiDamFileViewer,
    puiFileClassificationsTab,
    puiFileDeletionConfirmationDialog,
  },
  inject: ['dialogSelector'],
  props: {
    /**
     * If should show the expand, title, close container
     */
    showPanelControls: {
      type: Boolean,
      default: true,
    },
    /**
     * Files to display the details of
     */
    files: {
      type: Array,
      required: true,
    },
    /**
     * Panel title
     */
    title: {
      type: String,
      required: true,
    },
    /**
     * If the panel is expanded. This
     * will influence the number of attribute tabs
     * displayed
     */
    isExpanded: {
      type: Boolean,
      required: false,
    },
    /**
     * The request to execute api requests under.
     * Used to cancel requests
     */
    fileRequestNamespace: {
      type: String,
      required: true,
    },
    /**
     * If we should show the 'open selection dialog' action
     */
    showOpenSelectionDialog: {
      type: Boolean,
      required: false,
    },
    /**
     * If should hide the cancel/save attribute buttons
     */
    hideAttributeButtons: {
      type: Boolean,
      default: false,
    },
    /**
     * The folder being dragged
     */
    draggingFolder: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      // Carousel
      CAROUSEL_HEIGHT,
      carouselOptions: {
        // Type: 'loop' causes multiple clones of an instance of FilePanelSelection's DOM
        // without cloning the vue component: causing zombie dom elements
        type: 'slide',
        height: `${CAROUSEL_HEIGHT}px`,
        autoWidth: true,
        // Keep empty space next to first/last slide
        trimSpace: false,
      },
      // Definition for single item
      singleItemTabDefinition: singleItemTabDefinition(),
      // Definition for multiples files
      multipleItemTabDefinition: multipleItemTabDefinition(),
      // Form definition
      formDefinitionTabs: null,
      /**
       * Plain file objects: the saved files' state
       */
      sourceFiles: null,
      /**
       * Plain file objects: the editing files' state
       */
      updatedFiles: null,
      fileAttributeValuesUpdated: [],
      attributesAreLoading: false,
      getAttributesError: false,
      attributesAreDirty: false,
      importExternalFileCount: 0,
      cancelExternalFileImports: [],
      // ATTRS > OTHER
      validationResult: null,
      // MOVE
      moveIsDirty: false,
      moveObj: { associate: [], dissociate: [] },
      // ALL
      saveInProgress: false,
      // TABS
      activeAttributeTabId: null,
      currentDynamicTabId: null,
      CLASSIFICATIONS_TAB,
      DETAILS_TAB,
      extraTabs: [CLASSIFICATIONS_TAB, DETAILS_TAB],
      // OTHER
      Permissions,
    };
  },
  computed: {
    /**
     * @returns {number} Number of max items display in panel
     */
    maxItemsDisplay() {
      return FILE_PANEL_MAX_ITEMS_DISPLAY;
    },
    /**
     * @returns {array} the details definitions to display in the details tab
     */
    itemTabDefinition() {
      if (this.files.length <= 1) {
        return this.singleItemTabDefinition;
      }

      return this.multipleItemTabDefinition;
    },
    /**
     * @returns {boolean} if the attribute values are invalid
     */
    attributeValuesAreInvalid() {
      return !!this.validationResult && Object.keys(this.validationResult.errors).length > 0;
    },
    /**
     * @returns {number} the min amount of tabs that can be displayed
     */
    nbMinTabs() {
      const setting = services
        .getService('applications')
        .getAppDeepParameter(
          DAM_APP_NAME,
          'screensSettings',
          'assets.fileDetails.tabs.nbRetracted',
          true
        );

      return typeof setting === 'number' ? setting : null;
    },
    /**
     * @returns {number} the max amount of tabs that can be displayed
     */
    nbMaxTabs() {
      const setting = services
        .getService('applications')
        .getAppDeepParameter(
          DAM_APP_NAME,
          'screensSettings',
          'assets.fileDetails.tabs.nbExpanded',
          true
        );

      return typeof setting === 'number' ? setting : null;
    },
    /**
     * @returns {object[]} the files in reverse order for the carousel
     */
    carouselFiles() {
      // Our parent puts new items at the end of the array, but we want to display
      // the newly selected items at the start of the carousel
      return [...this.files].reverse();
    },
    /**
     * @returns {boolean} if any of the file values are dirty
     */
    valuesAreDirty() {
      return this.attributesAreDirty || this.moveIsDirty;
    },
    /**
     * @returns {boolean} if the save/cancel buttons should be displayed
     */
    showActionButtons() {
      return !this.hideAttributeButtons && this.valuesAreDirty && !this.saveInProgress;
    },
    /**
     * @returns {object[]} files that will be deleted upon saving their new
     * classifications state
     */
    filesToDelete() {
      return this.updatedFiles.filter(file => !file.paths || !file.paths.length);
    },
    /**
     * @returns {boolean} when saving the values, if the user should confirm
     * beforehand
     */
    saveRequiresConfirmation() {
      return this.filesToDelete.length;
    },
    /**
     * @returns {boolean} if any of the files' tasks are in progress
     */
    hasTasksInProgress() {
      return getDamService('fileTasks')
        .getCurrentFilesTasks(this.carouselFiles.map(f => f.itemId))
        .some(t => t.data.state() === FileTaskStatus.IN_PROGRESS);
    },
  },
  watch: {
    /**
     * Watches valuesAreDirty to emit its value
     */
    valuesAreDirty(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.$emit('valuesAreDirty', newVal);
      }
    },
    /**
     * Watches saveInProgress to emit its value
     */
    saveInProgress(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.$emit('saveInProgress', this.saveInProgress);
      }
    },
    /**
     * Emits 'importInProgress' with true, if there is at least 1 import in progress
     */
    importExternalFileCount(newVal) {
      this.$emit('importInProgress', newVal > 0);
    },
    /**
     * Watches the carousel files received to update the carousel position
     *
     * @param {object[]} nextValue - the new selection of files
     * @returns {void}
     */
    async carouselFiles(nextValue) {
      if (this.$refs.carousel && this.$refs.carousel.getLength() < nextValue.length) {
        // Wait for carousel to render new items
        await this.$nextTick();
        // If we added items to the selection, go to the last added item
        // which is at the first position in our carousel
        this.$refs.carousel.go(0, false);
      }
    },
  },
  methods: {
    // FILTERS
    piivoTranslate(value) {
      return Vue.filter('piivoTranslate')(value);
    },
    /**
     * Emits the 'openSelectionDialog' event
     *
     * @returns {void}
     */
    openSelectionDialog() {
      this.$emit('openSelectionDialog');
    },
    /**
     * Event toggle panel
     */
    toggleOpenPanel() {
      this.$emit('toggleOpen');
    },
    /**
     * Computes an info for file(s)
     *
     * @param {object[]} files - the files to compute the info for
     * @param {Object} info - info to compute file properties
     * @return {Object} - the value of the computed info
     */
    computeFileInfos(info) {
      return computeFileInfos(this.files, info);
    },
    /**
     * @param {object} file - a media file
     * @returns {string} unique id of the file
     */
    getFileId(file) {
      return file.itemId;
    },
    /**
     * @returns {object} style object for a carousel item
     */
    getThumbnailStyle(file) {
      const { height, width } = file.description;
      const carouselHeight = CAROUSEL_HEIGHT;

      return {
        // We need to return the width, since the carousel is using autoWidth
        width: `${(width * carouselHeight) / height}px`,
      };
    },
    /**
     * Cancel update attributes
     *
     */
    abandonChanges() {
      this.fileAttributeValuesUpdated = [];
      this.updatedFiles = JSON.parse(JSON.stringify(this.sourceFiles));

      this.attributesAreDirty = false;
      if (this.$refs.multipleAttributePanel) {
        this.$refs.multipleAttributePanel.forEach(ref => {
          if (ref.resetComputeValues) {
            ref.resetComputeValues(true); // Reset to source
          }
        });
      }

      this.moveIsDirty = false;
      if (this.$refs.classificationsTab) {
        this.$refs.classificationsTab.resetComputeValues(true); // Reset to source
      }
    },
    /**
     * Call API to get files attributes
     */
    async getFormAndValues() {
      if (!this.files.length || this.files.length >= this.maxItemsDisplay) {
        this.formDefinitionTabs = null;
        this.sourceFiles = [];
        this.updatedFiles = [];

        this.attributesAreDirty = false;
        this.moveIsDirty = false;

        this.attributesAreLoading = false;
        this.getAttributesError = false;

        return;
      }

      this.attributesAreLoading = true;
      this.getAttributesError = false;

      try {
        const response = await getFormAndValuesByItemsIds(this.fileRequestNamespace, this.files);
        this.formDefinitionTabs = response.form && response.form.tabs ? response.form.tabs : [];
        this.sourceFiles = response.files;
        this.updatedFiles = JSON.parse(JSON.stringify(this.sourceFiles));
        this.$emit('attributeSourceValuesChange', this.sourceFiles[0].attributes);
        this.attributesAreDirty = false;
        this.moveIsDirty = false;
        this.attributesAreLoading = false;
      } catch (error) {
        if (!Vue.http.isCancel(error)) {
          this.getAttributesError = true;
        }
        this.attributesAreLoading = false;
      }
    },
    /**
     * @param {object} attribute - the attribute whose options to retrieve
     * @param {object} parameters - parameters for the load
     * @param {object} parameters.context - additional context to load the values
     * @returns {Promise<object[]>} options for the attribute
     */
    async getPossibleValues(attribute, { context }) {
      if (attribute.type === AttributeTypes.LINKS) {
        return getAttributeLinkOptions(attribute);
      }

      return Promise.resolve([]);
    },
    /**
     * Imports external files to the attribute's storage
     *
     * @param {object} attribute - the attribute onto which we are uploading the files
     * @param {object[]} tempFiles - array of objects containing the file blobs to upload
     * @param {(tempFile, importedFile) => void} onFileUploadedCb - callback for when a file is finished uploading.
     * Receives the original temp file, and the uploaded file
     * @param {(cancelCb: (tempId?: string) => void) => void} provideAbortCb - callback
     * that proves the cancel function
     */
    async importExternalFiles(attribute, tempFiles, onFileUploadedCb, provideAbortCb) {
      // TODO: prevent changing page/selection
      this.importExternalFileCount++;

      let currentCancelCb = null;

      const removeCancelCb = () => {
        if (!currentCancelCb) {
          return;
        }
        const cancelIdx = this.cancelExternalFileImports.indexOf(currentCancelCb);
        if (cancelIdx !== -1) {
          this.cancelExternalFileImports.splice(cancelIdx, 1);
        }
      };

      try {
        const importedFiles = await getDamService('damAttributes').importExternalFiles(
          attribute,
          tempFiles,
          onFileUploadedCb,
          cancelCb => {
            currentCancelCb = cancelCb;
            this.cancelExternalFileImports.push(currentCancelCb);
            provideAbortCb(cancelCb);
          }
        );

        removeCancelCb();

        this.importExternalFileCount--;
        return importedFiles;
      } catch (err) {
        removeCancelCb();
        throw err;
      }
    },
    /**
     * Update attributes values
     * @params {Object} attribute - attribute definition
     * @params {Object|Boolean|String|Array} newValue - new attribute value
     * @returns {void}
     */
    attributeValueChanged(attribute, newValue) {
      this.attributesAreDirty = true;

      const existingValue = this.fileAttributeValuesUpdated.find(
        item => item.attributeId === attribute.itemId
      );
      // Value already exists
      if (existingValue) {
        existingValue.value = newValue;
      } else {
        this.fileAttributeValuesUpdated.push({ attributeId: attribute.itemId, value: newValue });
      }

      this.updatedFiles.forEach(file => {
        file.attributes.forEach(fileAttr => {
          if (fileAttr.attributeId === attribute.itemId) {
            fileAttr.value = newValue;
          }
        });
      });

      // Reset all panels across tabs so their values are
      // synchronized to 'updatedFiles'
      this.$refs.multipleAttributePanel.forEach(ref => {
        ref.resetComputeValues(false); // Reset to updated
      });

      this.validateAttributeValues();
    },
    /**
     * Updates the move object
     *
     * @param {object} moveObj - the move object of the
     * associate/dissociate changes
     */
    moveChanged(moveObj) {
      this.moveObj = moveObj;
      this.moveIsDirty = this.moveObj.associate.length || this.moveObj.dissociate.length;
    },
    /**
     * Checks if the attributes values are valid and then returns the data
     * to update them
     *
     * @returns {object} data for attribute update
     */
    getAttributeUpdateData() {
      if (this.importExternalFileCount) {
        services
          .getService('alerts')
          .alertWarning(
            this.$t('dam.files.panel_selection.attributes.save.prevents.import_in_progress')
          );
        throw new Error('Did not save attribute values: an import is in progress');
      }

      if (!this.attributesAreDirty) {
        return {
          fileIds: [],
          attributeValues: [],
        };
      }

      this.validateAttributeValues();
      if (this.attributeValuesAreInvalid) {
        services
          .getService('alerts')
          .alertError(this.$t('dam.files.panel_selection.attributes.save.prevents.invalid_values'));

        throw new Error('Did not save attribute values: there were invalid values');
      }

      return {
        fileIds: this.files.map(file => file.itemId),
        attributeValues: this.fileAttributeValuesUpdated,
      };
    },
    /**
     * Validates the values of all the attributes and saves the result
     *
     * @returns {void}
     */
    validateAttributeValues() {
      // Assume that before editing, all values are valid.
      // Thus, on change, since the changed attributes are synchronized
      // across all the files, we only need to validate the first file's values
      const firstFile = this.updatedFiles[0];
      if (!firstFile) {
        // If no file, nothing to validate
        this.validationResult = null;
        return;
      }

      this.validationResult = validateAttributes(firstFile.attributes, this.formDefinitionTabs, {
        validateInvisibleItems: false,
        breakOnFirstError: true,
      });
    },
    // TABS
    /**
     * @param {object} tab - a tab
     * @param {number} tabIndex - the index of the tab in the array
     * @returns {string} a unique id for the tab
     */
    getTabId(tab, tabIndex) {
      return tab.itemId;
    },
    /**
     * Triggered when the dynamic tab changes
     *
     * @param {object} tabId - the new dynamic tab id
     * @returns {void}
     */
    onChangeDynamicTab(tabId) {
      this.currentDynamicTabId = tabId;
    },
    /**
     * Triggered when the active attribute tab changes
     *
     * @param {object} tab - the new active tab
     * @param {number} tabIndex - the index of the tab
     * @returns {void}
     */
    onSelectAttributeTabChange(tab, tabIndex) {
      this.activeAttributeTabId = this.getTabId(tab);
    },
    /**
     * Triggered when the active attribute tab is cleared
     *
     * @param {object} oldTab - the old active tab
     * @returns {void}
     */
    onClearSelectedAttributeTab(oldTab) {
      this.activeAttributeTabId = null;
    },
    // ACTIONS
    shareItems() {},
    /**
     * Triggers a download of the files show in this panel
     *
     * @returns {void}
     */
    downloadFiles() {
      FileDownloadManager.downloadFiles(
        this.files.map(file => file.itemId),
        null
      );
    },
    moveItems() {},
    deleteItems() {},
    // OTHER
    /**
     * Saves all changes.
     * Should not be used from the outside, prefer executeConfirmableSave
     */
    async saveChanges() {
      this.saveInProgress = true;

      try {
        const { fileIds, attributeValues } = this.getAttributeUpdateData();
        const { associate, dissociate } = this.moveIsDirty
          ? this.moveObj
          : { associate: [], dissociate: [] };

        await FileUpdateManager.updateFiles(
          { fileIds, attributes: attributeValues },
          associate,
          dissociate,
          null,
          null
        );

        // Since updatedFiles contains the changes that will be saved, just update
        // sourceFiles to match it
        this.sourceFiles = JSON.parse(JSON.stringify(this.updatedFiles));

        this.attributesAreDirty = false;
        this.moveIsDirty = false;

        // Reset values updated
        this.fileAttributeValuesUpdated = [];
        this.$emit('attributeSourceValuesChange', this.sourceFiles[0].attributes);
      } catch (err) {
        services.getService('alerts').alertError(this.$t('dam.files.update.alert.error'));
      }

      this.saveInProgress = false;
    },
    // EXPOSED
    /**
     * Saves the files while confirming file deletion if necessary
     *
     * @param {() => void | Promise<void>} actionCb - called when the user confirms
     * file deletion
     * @returns {Promise<void>} resolves once the user cancels or confirms
     * file deletion
     */
    async executeConfirmableBreakingSave(actionCb = null) {
      if (!this.saveRequiresConfirmation) {
        // If there is nothing to confirm, save the changes
        // and then invoke the callback
        await this.saveChanges();
        if (actionCb) {
          return Promise.resolve(actionCb());
        }
        return Promise.resolve();
      }

      // Else make the user confirm the deletion
      return new Promise((resolve, reject) => {
        (() => {
          try {
            this.$refs.deletionConfirmationDialog.open(
              [...this.filesToDelete],
              () => {
                if (this.$refs.deletionConfirmationDialog) {
                  this.$refs.deletionConfirmationDialog.close();
                }
                resolve();
              },
              () => {
                if (this.$refs.deletionConfirmationDialog) {
                  this.$refs.deletionConfirmationDialog.close();
                }
                resolve(
                  this.saveChanges().then(res => {
                    if (actionCb) {
                      return actionCb();
                    }
                    return res;
                  })
                );
              }
            );
          } catch (err) {
            reject(err);
          }
        })();
      });
    },
    /**
     * Pass through method to update the form and values
     *
     * @returns {void}
     */
    updateForm() {
      return this.getFormAndValues();
    },
    /**
     * Will show a confirmation dialog if there are dirty attributes
     * or something that prevents a save. On save/ignore, the callback will be invoked
     *
     * @param {() => void} actionCallback - the function to call after the user indicates to discard
     * the unsaved changes, or after the changes are saved, or if there is nothing to save at all
     */
    executeConfirmableAction(actionCallback) {
      if (!this.attributesAreDirty && !this.moveIsDirty && this.importExternalFileCount <= 0) {
        actionCallback();
        return;
      }

      this.$refs.unsavedAttributesDialog.open({
        onCancel: () => {
          // Stops action
          if (this.$refs.unsavedAttributesDialog) {
            this.$refs.unsavedAttributesDialog.close();
          }
        },
        onCancelUpdate: async () => {
          // Abandons changes and continues
          this.abandonChanges();
          if (this.$refs.unsavedAttributesDialog) {
            this.$refs.unsavedAttributesDialog.close();
            await this.$nextTick(); // Wait for update dialog to close
          }
          actionCallback();
        },
        onSaveUpdate: async () => {
          // Saves and continues
          try {
            if (this.$refs.unsavedAttributesDialog) {
              this.$refs.unsavedAttributesDialog.close();
              await this.$nextTick(); // Wait for update dialog to close
            }
            await this.executeConfirmableBreakingSave(actionCallback);
          } catch (err) {
            // A save error should've already been handled
          }
        },
      });
    },
  },
  /**
   * Component mounted hook
   */
  mounted() {
    this.getFormAndValues();
  },
};
</script>
